import { LitElement, html, nothing } from "lit";
import { fa, onceParentAvailable } from "../../../utils/widgets.js";
import {
	faBackward, faEllipsisVertical, faForward, faPause, faPlay
} from "@fortawesome/free-solid-svg-icons";
import PlayerStyles from "./Player.css" with { type: "css" };
document.adoptedStyleSheets.push(PlayerStyles);

/**
 * Player widget shows prev/play/pause/next/more buttons in a unified toolbar.
 * @class Panoramax.components.ui.widgets.Player
 * @element pnx-widget-player
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-widget-player _parent=${viewer} />
 * ```
 */
export default class Player extends LitElement {
	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.widgets.Player#
	 * @type {Object}
	 * @property {number} [playing=0] Is sequence currently playing ? 0 = no, 1 = plays forward, -1 = plays backward
	 * @property {string} [size=xl] Group size (md, xl)
	 * @property {string} [more=depends] Should we display the "more options" button ("always", "never", "depends" = only on large screens (default))
	 */
	static properties = {
		playing: {type: Number, reflect: true},
		size: {type: String},
		more: {type: String},
		_activePrev: {state: true},
		_activePlayBack: {state: true},
		_activePlay: {state: true},
		_activeNext: {state: true},
	};

	constructor() {
		super();

		this.playing = 0;
		this.size = "xl";
		this.more = "depends";
		this._activePrev = true;
		this._activePlayBack = true;
		this._activePlay = true;
		this._activeNext = true;
	}

	/** @private */
	createRenderRoot() {
		return this;
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();

		onceParentAvailable(this).then(() => this._parent?.oncePSVReady?.().then(() => {
			this._parent.psv.addEventListener("sequence-playing", e => this.playing = e.detail.backward ? -1 : 1);
			this._parent.psv.addEventListener("sequence-stopped", () => this.playing = 0);
			this._parent.psv.addEventListener("picture-loaded", this._changeActiveStates.bind(this));
			this._parent.psv.addEventListener("pictures-navigation-changed", this._changeActiveStates.bind(this));
			this._changeActiveStates();
		}));
	}

	/** @private */
	_changeActiveStates() {
		if(this._parent.psv.getPicturesNavigation?.() === "pic") {
			this._activeNext = false;
			this._activePlayBack = false;
			this._activePlay = false;
			this._activePrev = false;
		}
		else {
			// eslint-disable-next-line eqeqeq
			this._activePrev = this._parent.psv.getPictureMetadata()?.sequence?.prevPic != null;
			this._activePlayBack = this._activePrev;

			// eslint-disable-next-line eqeqeq
			this._activeNext = this._parent.psv.getPictureMetadata()?.sequence?.nextPic != null;
			this._activePlay = this._activeNext;
		}
		this.requestUpdate();
	}

	/** @private */
	_onPlayClick() {
		if(this._parent?.psv?.isSequencePlaying() && this.playing === 1) {
			this.playing = 0;
			this._parent?.psv?.stopSequence();
		}
		else {
			this.playing = 1;
			this._parent?.psv?.playSequence();
		}
	}

	/** @private */
	_onPlayBackClick() {
		if(this._parent?.psv?.isSequencePlaying() && this.playing === -1) {
			this.playing = 0;
			this._parent?.psv?.stopSequence();
		}
		else {
			this.playing = -1;
			this._parent?.psv?.playSequence(true);
		}
	}

	/** @private */
	render() {
		if(!this._parent?.psv?.addEventListener) { return nothing; }

		return html`
			<pnx-button-group
				id="pnx-widget-player"
				dir="row"
				size=${this.size}
				class="pnx-print-hidden"
				style=${!this._activePrev && !this._activePlay && !this._activeNext ? "display: none": ""}
			>
				<pnx-button
					kind="superflat"
					id="pnx-player-prev"
					title=${this._parent?._t.pnx.sequence_prev}
					@click=${() => this._parent?.psv?.goToPrevPicture()}
					.disabled=${!this._activePrev}
				>${fa(faBackward)}</pnx-button>

				<pnx-button
					kind="superflat"
					id="pnx-player-play-backward"
					title=${this.playing === -1 ? this._parent?._t.pnx.sequence_pause : this._parent?._t.pnx.sequence_play_backwards}
					.disabled=${!this._activePlayBack}
					@click=${this._onPlayBackClick.bind(this)}
				>${this.playing === -1 ? fa(faPause) : fa(faPlay, {transform: {rotate: 180}})}</pnx-button>

				<pnx-button
					kind="superflat"
					id="pnx-player-play"
					title=${this.playing === 1 ? this._parent?._t.pnx.sequence_pause : this._parent?._t.pnx.sequence_play}
					.disabled=${!this._activePlay}
					@click=${this._onPlayClick.bind(this)}
				>${this.playing === 1 ? fa(faPause) : fa(faPlay)}</pnx-button>

				<pnx-button
					kind="superflat"
					id="pnx-player-next"
					title=${this._parent?._t.pnx.sequence_next}
					.disabled=${!this._activeNext}
					@click=${() => this._parent?.psv?.goToNextPicture()}
				>${fa(faForward)}</pnx-button>

				${this.more === "never" || (this.more !== "always" && this._parent?.isWidthSmall()) ? nothing : html`
					<pnx-togglable-group padded="false" ._parent=${this._parent}>
						<pnx-button
							kind="flat"
							id="pnx-player-more"
							title=${this._parent?._t.pnx.sequence_more}
							class=${this.more === "depends" ? "pnx-xs-hidden" : ""}
							slot="button"
						>${fa(faEllipsisVertical)}</pnx-button>

						<pnx-player-options ._parent=${this._parent}></pnx-player-options>
					</pnx-togglable-group>
				`}
			</pnx-button-group>
		`;
	}
}

customElements.define("pnx-widget-player", Player);
